import { z } from "zod";
import { formatMatchValues, isPresenceOp, isRegexOp, parseMatchValues, patternError } from "@/lib/match-fields";
import {
  EntryFallback,
  EntryFallbackKind,
  EntryPointInput,
  EntryRule,
  EntrySelector,
  EntrySelectorKind,
  type EntryPoint,
} from "@gen/tracker/v1/entry_point_pb";

// Zod mirror of tracker.v1.EntryPointInput (docs/specs/use-cases/entry-point.md).
// The service stays authoritative (domain.ValidateEntryPoint); this catches
// the obvious before the round-trip and maps the form to and from the proto.

/** domain.MaxEntryRules / MaxEntryStickySecs. */
export const ENTRY_RULES_MAX = 20;
export const ENTRY_STICKY_MAX = 2_592_000;
/** How long a returning visitor keeps their campaign without the token, in the form's choices. */
export const ENTRY_STICKY_CHOICES = [0, 3600, 86_400, 604_800, ENTRY_STICKY_MAX] as const;
export const ENTRY_FALLBACKS = ["origin", "not_found", "redirect", "campaign"] as const;
export type EntryFallbackName = (typeof ENTRY_FALLBACKS)[number];

const PARAM_RE = /^[A-Za-z0-9_.-]{1,100}$/;
/** The token in `?<token>` (domain.ValidEntryToken). */
export const ENTRY_TOKEN_RE = /^[A-Za-z0-9_.-]{1,100}$/;

const ruleSchema = z
  .object({
    /** A fixed match field (country, os…) or "param" — the link parameter named in `param`. */
    field: z.string().min(1),
    param: z.string(),
    op: z.string().min(1),
    values: z.string(),
    campaignId: z.string().min(1, "Выберите кампанию"),
  })
  .superRefine((r, ctx) => {
    if (r.field === "param" && !PARAM_RE.test(r.param.trim())) ctx.addIssue({ code: "custom", path: ["param"], message: "Буквы, цифры, _ . - (до 100)" });
    const values = parseMatchValues(r.op, r.values);
    if (isPresenceOp(r.op)) return;
    if (values.length === 0) ctx.addIssue({ code: "custom", path: ["values"], message: "Хотя бы одно значение" });
    if (isRegexOp(r.op)) {
      const bad = values.map(patternError).find(Boolean);
      if (bad) ctx.addIssue({ code: "custom", path: ["values"], message: bad });
    }
  });

export const entryPointFormSchema = z
  .object({
    name: z.string().trim().min(1, "Название").max(200),
    path: z.string().trim().max(65),
    /** `?<token>` — the token is the parameter's name. */
    byKey: z.boolean(),
    /** `?<param>=<token>`; "" = off. */
    byParam: z.string().trim(),
    rules: z.array(ruleSchema).max(ENTRY_RULES_MAX),
    stickySeconds: z.number().int().min(0).max(ENTRY_STICKY_MAX),
    fallback: z.enum(ENTRY_FALLBACKS),
    fallbackUrl: z.string().trim(),
    fallbackCampaignId: z.string(),
    active: z.boolean(),
  })
  .superRefine((v, ctx) => {
    if (!v.byKey && !v.byParam) ctx.addIssue({ code: "custom", path: ["byKey"], message: "Хотя бы один способ узнать кампанию по ссылке" });
    if (v.byParam && !PARAM_RE.test(v.byParam)) ctx.addIssue({ code: "custom", path: ["byParam"], message: "Буквы, цифры, _ . - (до 100)" });
    if (v.fallback === "redirect" && !/^https:\/\/\S+$/.test(v.fallbackUrl)) ctx.addIssue({ code: "custom", path: ["fallbackUrl"], message: "Адрес https://…" });
    if (v.fallback === "campaign" && !v.fallbackCampaignId) ctx.addIssue({ code: "custom", path: ["fallbackCampaignId"], message: "Выберите кампанию" });
  });

export type EntryPointFormValues = z.infer<typeof entryPointFormSchema>;
export type EntryRuleFormValues = EntryPointFormValues["rules"][number];

const FALLBACK_KIND: Record<EntryFallbackName, EntryFallbackKind> = {
  origin: EntryFallbackKind.ORIGIN,
  not_found: EntryFallbackKind.NOT_FOUND,
  redirect: EntryFallbackKind.REDIRECT,
  campaign: EntryFallbackKind.CAMPAIGN,
};

/** A new entry point: the domain's root, picked by `?<token>`, the rest to the site. */
export const NEW_ENTRY_POINT: EntryPointFormValues = {
  name: "",
  path: "/",
  byKey: true,
  byParam: "",
  rules: [],
  stickySeconds: 86_400,
  fallback: "origin",
  fallbackUrl: "",
  fallbackCampaignId: "",
  active: true,
};

/** The form of a stored entry point. Pure. */
export function entryPointToForm(e: EntryPoint): EntryPointFormValues {
  const fallback = (Object.keys(FALLBACK_KIND) as EntryFallbackName[]).find((k) => FALLBACK_KIND[k] === e.fallback?.kind) ?? "origin";
  return {
    name: e.name,
    path: e.path || "/",
    byKey: e.selectors.some((s) => s.kind === EntrySelectorKind.QUERY_KEY),
    byParam: e.selectors.find((s) => s.kind === EntrySelectorKind.QUERY_VALUE)?.param ?? "",
    rules: e.rules.map((r) => ({
      field: r.field.startsWith("param:") ? "param" : r.field,
      param: r.field.startsWith("param:") ? r.field.slice("param:".length) : "",
      op: r.op,
      values: formatMatchValues(r.op, r.values),
      campaignId: r.campaignId,
    })),
    stickySeconds: e.stickySeconds,
    fallback,
    fallbackUrl: e.fallback?.url ?? "",
    fallbackCampaignId: e.fallback?.campaignId ?? "",
    active: e.status !== "PAUSED",
  };
}

/** The proto input of the form: the key selector first, as the edge tries them in order. Pure. */
export function formToEntryPointInput(v: EntryPointFormValues): EntryPointInput {
  const selectors: EntrySelector[] = [];
  if (v.byKey) selectors.push(new EntrySelector({ kind: EntrySelectorKind.QUERY_KEY }));
  if (v.byParam) selectors.push(new EntrySelector({ kind: EntrySelectorKind.QUERY_VALUE, param: v.byParam }));
  return new EntryPointInput({
    name: v.name.trim(),
    path: v.path.trim() || "/",
    selectors,
    rules: v.rules.map(
      (r) =>
        new EntryRule({
          field: r.field === "param" ? `param:${r.param.trim()}` : r.field,
          op: r.op,
          values: isPresenceOp(r.op) ? [] : parseMatchValues(r.op, r.values),
          campaignId: r.campaignId,
        }),
    ),
    stickySeconds: v.stickySeconds,
    fallback: new EntryFallback({
      kind: FALLBACK_KIND[v.fallback],
      url: v.fallback === "redirect" ? v.fallbackUrl : "",
      campaignId: v.fallback === "campaign" ? v.fallbackCampaignId : "",
    }),
    status: v.active ? "ACTIVE" : "PAUSED",
  });
}

